import { useCallback, useEffect, useMemo } from 'react';
import { EventEmitter } from 'events';
async function sleep(timeout) {
    return new Promise((resolve) => {
        setTimeout(resolve, timeout);
    });
}
/**
 * Has the recpatcha <script /> tag loaded
 */
function isLoaded() {
    return (typeof window !== 'undefined' &&
        typeof window.grecaptcha !== 'undefined' &&
        typeof window.grecaptcha.ready === 'function');
}
/**
 * Waits 200ms for the recpatcha <script /> tag to load
 */
async function waitForLoaded() {
    let loaded = isLoaded();
    let tries = 1;
    while (!loaded && tries < 20) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(10);
        tries += 1;
        loaded = isLoaded();
    }
    return loaded;
}
/**
 * Global timer has elapsed
 */
function timedOut(reject) {
    const err = new Error('Timed out waiting for recaptcha token');
    console.error(err);
    reject(err);
}
/**
 * Handler for when we recieve tokens from the emitter
 */
function handleTokenEvent(resolve, token, timeout) {
    if (token && typeof token === 'string') {
        clearTimeout(timeout);
        resolve(token);
    }
    else {
        console.error('recieved invalid token event', token);
    }
}
/**
 * Handler for when we recieve errors from the emitter
 */
function handleErrorEvent(timeout, reject, reason) {
    clearTimeout(timeout);
    const err = new Error(`An error occured witch Recaptcha: ${reason?.message ?? '[NO MESSAGE]'}`);
    console.error(err);
    reject(err);
}
/**
 * Hook for easy interaction with recaptcha
 */
export function useCaptchaTokens() {
    /**
     * An emitter for the calling component's recaptcha interactions
     */
    const captchaTokenEvents = useMemo(() => new EventEmitter(), []);
    /**
     * Cleanup on dismount
     */
    useEffect(() => {
        return () => {
            captchaTokenEvents.removeAllListeners();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /**
     * Cached event emitter
     */
    const emitCaptchaEvent = useCallback((...args) => {
        return captchaTokenEvents.emit(...args);
    }, [captchaTokenEvents]);
    /**
     * Asyncronously gets a recpatcha token
     */
    const getCaptchaToken = useCallback(async (widgetId) => {
        if (widgetId == null) {
            throw new Error('Recaptcha element has not mounted');
        }
        // eslint-disable-next-line consistent-return, @typescript-eslint/no-misused-promises
        return new Promise(async (resolve, reject) => {
            /**
             * Setup our total timeout timer
             */
            const timeout = setTimeout(() => timedOut(reject), 30 * 1000);
            /**
             * Prep our error handling
             */
            captchaTokenEvents.on('error', (err) => {
                handleErrorEvent(timeout, reject, err);
            });
            /**
             * Wait for the <script /> tag to finish loading
             */
            const loaded = await waitForLoaded();
            if (!loaded) {
                captchaTokenEvents.emit('error', new Error('ReCaptcha did not load'));
                return;
            }
            /**
             * Listen for tokens
             */
            captchaTokenEvents.once('token', (token) => {
                handleTokenEvent(resolve, token, timeout);
            });
            /**
             * Reset Recaptcha when loaded and then ask for puzzle solution
             */
            window.grecaptcha.ready(() => {
                window.grecaptcha.reset(widgetId);
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                window.grecaptcha.execute(widgetId);
            });
        });
    }, [captchaTokenEvents]);
    return {
        getCaptchaToken,
        emitCaptchaEvent,
    };
}
